import type { OperationManifest, ConnectorInfo } from '@/types/operationManifest';
import { SettingScope } from '@/types/operationManifest';

/**
 * Invoke Workflow Manifest
 *
 * Defines the input/output schema for the Workflow action type.
 * This action calls another Logic App workflow.
 *
 * Following LogicAppsUX pattern from:
 * libs/logic-apps-shared/src/designer-client-services/lib/consumption/manifests/invokeWorkflow.ts
 */

// Brand color and icon for the sub-workflow concept. Mirrors Fluent's
// OrganizationHorizontalRegular glyph so the action node, agent tool node,
// and dock toolbar / quick-add panel all read as the same thing.
const BRAND_COLOR = '#107C10';
const ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect width="24" height="24" rx="3" fill="${BRAND_COLOR}"/><g transform="translate(2,2)" fill="#fff"><path d="M2 10a3 3 0 0 0 5.96.5H9.5v3c0 .83.67 1.5 1.5 1.5h1.04a3 3 0 1 0 0-1H11a.5.5 0 0 1-.5-.5v-7c0-.28.22-.5.5-.5h1.04a3 3 0 1 0 0-1H11c-.83 0-1.5.67-1.5 1.5v3H7.96A3 3 0 0 0 2 10Zm3 2a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm10 4.5a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm-2-11a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"/></g></svg>`;
const workflowIconSvg = `data:image/svg+xml,${encodeURIComponent(ICON_SVG)}`;

// Connector group for workflow operations
const invokeWorkflowGroup: ConnectorInfo = {
  id: 'connectionProviders/localWorkflowOperation',
  name: 'Workflow Operations',
  properties: {
    description: 'Call another Logic App workflow',
    displayName: 'Workflow Operations',
    iconUri: workflowIconSvg,
    brandColor: BRAND_COLOR,
  },
};

export default {
  properties: {
    iconUri: workflowIconSvg,
    brandColor: BRAND_COLOR,
    summary: 'Invoke a workflow',
    description: 'Call another Logic App workflow',

    inputs: {
      type: 'object',
      required: ['host'],
      properties: {
        host: {
          type: 'object',
          required: ['workflow'],
          properties: {
            workflow: {
              type: 'object',
              required: ['id'],
              properties: {
                id: {
                  title: 'Workflow',
                  description: 'The workflow to invoke',
                  type: 'string',
                  'x-ms-visibility': 'important',
                  'x-ms-editor': 'workflowpicker',
                },
              },
            },
            triggerName: {
              title: 'Trigger Name',
              description: 'The name of the trigger to invoke (optional)',
              type: 'string',
              'x-ms-visibility': 'advanced',
            },
          },
        },
        body: {
          title: 'Body',
          description: 'The request body to send to the workflow',
          type: 'object',
          'x-ms-visibility': 'important',
        },
        headers: {
          type: 'object',
          title: 'Headers',
          description: 'Additional headers to send with the request',
          'x-ms-visibility': 'advanced',
          'x-ms-editor': 'dictionary',
          'x-ms-editor-options': {
            valueType: 'string',
          },
        },
      },
    },
    inputsLocation: ['inputs'],
    isInputsOptional: false,

    outputs: {
      type: 'object',
      properties: {
        body: {
          title: 'Body',
          description: 'The response body from the workflow',
        },
        headers: {
          type: 'object',
          title: 'Headers',
          description: 'The response headers',
        },
        statusCode: {
          type: 'integer',
          title: 'Status Code',
          description: 'The HTTP status code of the response',
        },
      },
    },
    isOutputsOptional: false,
    includeRootOutputs: true,

    connector: invokeWorkflowGroup,

    settings: {
      operationOptions: {
        options: ['DisableAsyncPattern', 'DisableAutomaticDecompression'],
        scopes: [SettingScope.Action],
      },
      retryPolicy: {
        scopes: [SettingScope.Action],
      },
      secureData: {},
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
      timeout: {
        scopes: [SettingScope.Action],
      },
    },
  },
} as OperationManifest;
